import classNames from "classnames";
import Select, { SelectProps } from "rc-select";
import "rc-select/assets/index.css";
import { CustomTagProps, DisplayValueType } from "rc-select/lib/BaseSelect";
import React, { useEffect, useRef, useState } from "react";
import KuikaCheckbox from "../kuika-checkbox/kuika-checkbox";
import "../kuika-input/kuika-input.scss";
import {
  KuikaPopupContainerGetter,
  useKuikaPopupContainer
} from "../kuika-popup-container/kuika-popup-container-context";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaTooltip from "../kuika-tooltip/kuika-tooltip";
import KuikaSmallBoldText from "../typography/SmallBoldText/SmallBoldText";
import KuikaText from "../typography/Text/Text";
import "./kuika-selectbox.scss";

const getStringFromReactNode = (node: React.ReactNode): string => {
  if (!node) return "";
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getStringFromReactNode).join("");
  }
  if (React.isValidElement(node)) {
    return getStringFromReactNode(node.props.children);
  }
  return "";
};

export interface TreeNode {
  id: string;
  name: string;
  type: "folder" | "role";
  children?: TreeNode[];
  roleIds?: string[];
  value?: string | number;
  label?: string | React.ReactNode;
}

/**
 * rc-select 14 refuses a `null` option value — it warns and the option stops matching — but "no
 * setting yet" is a real choice in this product (the config panes' "Default" row), and it has to
 * reach the form store as `null`, not as an empty string that dirty-checking would read as a
 * change. So `null` is carried here and swapped for a private sentinel on the way into rc-select,
 * then swapped back on the way out. Callers never see the sentinel.
 */
const NULL_VALUE_SENTINEL: string = "__kuika-selectbox-null__";

/** What a selectbox can be showing: one value, several, or nothing chosen. */
export type KuikaSelectboxSelection = string | number | (string | number)[] | null;

/** Written onto an option's row so the list can tell which one the pointer is over. */
const OPTION_VALUE_ATTRIBUTE: string = "data-kuika-option-value";
const OPTION_VALUE_SELECTOR: string = `[${OPTION_VALUE_ATTRIBUTE}]`;

const toInnerValue = (value: string | number | null | undefined): string | number | undefined =>
  value === null ? NULL_VALUE_SENTINEL : (value ?? undefined);

const fromInnerValue = (value: unknown): unknown =>
  value === NULL_VALUE_SENTINEL
    ? null
    : Array.isArray(value)
      ? value.map((item: unknown): unknown => (item === NULL_VALUE_SENTINEL ? null : item))
      : value;

export interface KuikaSelectboxOption {
  key?: string;
  value: string | number | null;
  label: string | React.ReactNode;
  searchLabel?: string;
  disabled?: boolean;
  /**
   * Optional `data-testid` for this option's rendered row. rc-select forwards `data-*`
   * through `pickAttrs`, so the attribute lands on the option element itself — which is
   * the only stable way to pick a NAMED option out of a non-searchable selectbox.
   */
  testId?: string;
}

export interface KuikaSelectboxOptionGroup {
  label: string;
  options: KuikaSelectboxOption[];
}

/**
 * The chip budget handed to rc-select. A caller's own value wins; `showCountOnly` folds every chip
 * into the count; otherwise a `multiple`/`treeView` control — pinned to one row by its size rung —
 * shows as many chips as fit and says "+N" for the rest. Left unset, rc-select renders every chip
 * and its vendor sheet wraps them onto rows drawn outside the control. `tags` grows instead.
 */
export const resolveMaxTagCount = (
  mode: KuikaSelectboxProps["mode"],
  customMaxTagCount: number | "responsive" | undefined,
  showCountOnly: boolean
): number | "responsive" | undefined => {
  if (customMaxTagCount !== undefined) return customMaxTagCount;
  if (showCountOnly) return 0;
  if (mode === "multiple" || mode === "treeView") return "responsive";
  return undefined;
};

export interface KuikaSelectboxProps {
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  onChange?: (value: string | number | Array<string | number> | null | any) => void;
  /**
   * Fires for every pick, including one that repeats the value already selected — the case
   * `onChange` cannot report, because nothing changed. `onDeselect` is its counterpart in
   * `multiple`/`tags` mode. The option handed back is the caller's own entry from `options`
   * or `groups`, resolved by value; rc-select's internal option shape never reaches a caller.
   */
  onSelect?: (value: string | number | null, option: KuikaSelectboxOption | undefined) => void;
  onDeselect?: (value: string | number | null, option: KuikaSelectboxOption | undefined) => void;
  /**
   * Fires as the pointer enters an option's row — the whole row, padding included.
   *
   * The action panel's pickers name components on the canvas, and hovering a name lights that
   * component up so you can tell two "Label"s apart without picking one. antd carried this by
   * spreading whatever was left on an `<Option>` onto the rendered row; rc-select 14 keeps only
   * `aria-*`, `data-*` and `role`, so the row is tagged with its value and the list listens for
   * the pointer on the caller's behalf. Delegating also survives the virtual list recycling rows.
   */
  onOptionMouseEnter?: (value: string | number | null, option: KuikaSelectboxOption | undefined) => void;
  /**
   * Paints a pick straight away instead of waiting for the caller's `value` to catch up.
   *
   * By default a caller that passes `value` owns the selection, so a pick it declines is a pick
   * the field does not show — which is what a panel wanting to say only what it saved needs.
   * A caller whose value cannot come back in the same render, because a server has to answer
   * first, can ask for the pick to show meanwhile. It still follows `value` whenever that moves;
   * it only stops waiting for it. Use it where the round trip is real, not to paper over a write
   * that was refused.
   */
  optimisticSelection?: boolean;
  value?: string | number | Array<string | number> | null | any;
  size?: "sm" | "md" | "lg";
  options?: KuikaSelectboxOption[];
  groups?: KuikaSelectboxOptionGroup[];
  defaultValue?: string | number | Array<string | number>;
  mode?: "combobox" | "multiple" | "tags" | "treeView";
  /**
   * The options are suggestions, not a permitted set: whatever is typed is the value, and the
   * list only shortens the typing. Used where a field takes an open id — a Vertex AI location,
   * a header name — and a fixed list would be wrong.
   */
  freeText?: boolean;
  showCountOnly?: boolean;
  showTagCrossIcon?: boolean;
  allowClear?: boolean;
  onClear?: () => void;
  tagRender?: (props: CustomTagProps) => React.ReactElement;
  searchable?: boolean;
  dropdownHeader?: React.ReactNode;
  dropdownFooter?: React.ReactNode;
  /**
   * What the open list shows when it has nothing to list. Tree view draws its own list, so it
   * renders this in place of the tree; the other modes hand it to rc-select.
   */
  notFoundContent?: React.ReactNode;
  onDropdownHeaderClick?: (e: React.MouseEvent<HTMLElement>) => void;
  dropdownHeaderClassName?: string;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  onSearch?: (value: string) => void;
  searchValue?: string;
  suffixIcon?: React.ReactNode;
  prefix?: React.ReactNode;
  handleKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  tokenSeparators?: string[];
  preventTagCreation?: boolean;
  className?: string;
  overlayClassName?: string;
  /** Receives `null` for the option that stands for "no value" — the caller decides whether it shows. */
  filterValue?: (value: string | number | null) => boolean;
  filterOption?: (input: string, option: any) => boolean;
  treeData?: TreeNode[];
  expandedKeys?: string[];
  onExpandedKeysChange?: (keys: string[]) => void;
  onTreeNodeSelect?: (node: TreeNode, checked: boolean) => void;
  id?: string;
  showFilterResetButton?: boolean;
  filterResetTooltip?: string;
  /**
   * How many chips a `multiple`/`treeView` control shows before folding the rest into "+N".
   * Defaults to `"responsive"` in those modes — as many as fit on the control's one row — so a
   * long selection never draws past the control's fixed height. `tags` grows instead.
   */
  maxTagCount?: number | "responsive";
  maxTagPlaceholder?: ((omittedValues: any[]) => React.ReactNode) | React.ReactNode;
  /**
   * Where the options list mounts. Defaults to the nearest KuikaPopupContainerContext (a
   * KuikaDrawer provides one), else rc-select's `document.body`.
   */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  [key: string]: any;
  countLabel?: string;
  showOptionCheckboxes?: boolean;
}

const KuikaSelectbox: React.FC<KuikaSelectboxProps> = ({
  style = {},
  size = "md",
  options = [],
  groups,
  disabled = false,
  placeholder = "Select",
  onChange,
  onSelect,
  onDeselect,
  onOptionMouseEnter,
  optimisticSelection = false,
  value,
  defaultValue,
  mode = "combobox",
  freeText = false,
  showCountOnly,
  showTagCrossIcon = true,
  allowClear = false,
  onClear,
  tagRender,
  searchable = false,
  dropdownHeader,
  dropdownFooter,
  notFoundContent,
  onDropdownHeaderClick,
  dropdownHeaderClassName,
  visible,
  onVisibleChange,
  onSearch,
  searchValue,
  suffixIcon,
  prefix,
  handleKeyDown,
  tokenSeparators = [],
  preventTagCreation = false,
  className,
  overlayClassName,
  filterValue,
  filterOption: customFilterOption,
  treeData = [],
  expandedKeys = [],
  onExpandedKeysChange,
  onTreeNodeSelect,
  id,
  showFilterResetButton = false,
  filterResetTooltip = "Clear filter",
  maxTagCount: customMaxTagCount,
  maxTagPlaceholder: customMaxTagPlaceholder,
  countLabel,
  showOptionCheckboxes = false,
  getPopupContainer,
  ...rest
}) => {
  const popupContainerFromContext: KuikaPopupContainerGetter | undefined = useKuikaPopupContainer();
  const resolvedGetPopupContainer: ((triggerNode: HTMLElement) => HTMLElement) | undefined =
    getPopupContainer ?? popupContainerFromContext;
  // Only the config-style pickers pass an option whose value is `null` ("Default", "Inherited").
  // `null` is also this component's own "nothing selected", which is what draws the placeholder,
  // so the two are kept apart by storing the INNER value: an explicit null from the caller becomes
  // the sentinel, while an absent value stays null.
  const hasNullOption: boolean =
    options.some((option: KuikaSelectboxOption): boolean => option.value === null) ||
    (groups ?? []).some((group: KuikaSelectboxOptionGroup): boolean =>
      group.options.some((option: KuikaSelectboxOption): boolean => option.value === null)
    );
  const asInnerSelection = (next: KuikaSelectboxSelection): KuikaSelectboxSelection =>
    next === null && hasNullOption ? NULL_VALUE_SENTINEL : next;

  const [uncontrolledValue, setUncontrolledValue] = useState<KuikaSelectboxSelection>(
    value !== undefined
      ? asInnerSelection(value)
      : defaultValue || (mode === "multiple" || mode === "treeView" ? [] : null)
  );

  // An optimistic field still follows the caller whenever the caller moves the value. It only
  // stops waiting for that before painting the pick.
  useEffect(() => {
    if (!optimisticSelection || value === undefined) return;
    setUncontrolledValue(asInnerSelection(value));
  }, [optimisticSelection, value]);

  // A caller that stops passing a value it used to pass has cleared the field (a panel dropping the
  // property from its model), not handed the selection back: falling back to the last pick held here
  // would show a setting nothing saved, and re-picking it would fire no change. rc-select's own
  // merged state empties the same way.
  const previousValueRef: React.MutableRefObject<KuikaSelectboxSelection | undefined> = useRef<
    KuikaSelectboxSelection | undefined
  >(value);
  useEffect((): void => {
    if (previousValueRef.current !== undefined && value === undefined) {
      setUncontrolledValue(mode === "multiple" || mode === "treeView" ? [] : null);
    }
    previousValueRef.current = value;
  }, [value, mode]);

  /**
   * A caller that passes `value` owns the selection, so the control shows what that caller holds
   * — including when it declines to move it. Several panels write through a guard and keep the
   * old value when the guard says no; holding the clicked value here instead would leave the
   * field claiming a setting that was never saved.
   */
  const isCallerOwned: boolean = value !== undefined && !optimisticSelection;
  const selectedValue: KuikaSelectboxSelection = isCallerOwned ? asInnerSelection(value) : uncontrolledValue;
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const [internalExpandedKeys, setInternalExpandedKeys] = useState<string[]>(expandedKeys);

  const isOpen = visible !== undefined ? visible : isOpenInternal;

  const _showCountOnly = !!showCountOnly && (mode === "tags" || mode === "treeView" || mode === "multiple");
  const selectedCount = Array.isArray(selectedValue) ? selectedValue.length : 0;
  const renderSelectedCount = (omittedValues: any[]) => {
    const count = omittedValues?.length ?? selectedCount;
    if (!count) return null;
    // Use custom countLabel if provided, otherwise default to "selected"
    const label = countLabel || "selected";
    return (
      <span className="kuika-selectbox__selected-count" aria-label={`${count} ${label}`} tabIndex={0}>
        {count} {label}
      </span>
    );
  };

  const renderOmittedCount = (omittedValues: DisplayValueType[]): React.ReactNode => (
    <span className="kuika-selectbox__tag kuika-selectbox__tag--rest">+{omittedValues.length}</span>
  );

  const finalMaxTagCount: number | "responsive" | undefined = resolveMaxTagCount(
    mode,
    customMaxTagCount,
    _showCountOnly
  );
  let finalMaxTagPlaceholder: SelectProps["maxTagPlaceholder"];
  if (customMaxTagPlaceholder !== undefined) {
    finalMaxTagPlaceholder = customMaxTagPlaceholder;
  } else if (_showCountOnly) {
    finalMaxTagPlaceholder = renderSelectedCount;
  } else if (finalMaxTagCount !== undefined) {
    finalMaxTagPlaceholder = renderOmittedCount;
  }

  // The internal state is tracked whether or not anyone is listening: a caller that wants to know
  // when the list opens is not thereby claiming to drive it. Skipping the write left a selectbox
  // given `onVisibleChange` but no `visible` unable to open at all, since `isOpen` then read a
  // state nothing ever set. A caller that does pass `visible` still wins — `isOpen` prefers it.
  const handleVisibleChange = (newVisible: boolean): void => {
    setIsOpenInternal(newVisible);
    if (onVisibleChange) onVisibleChange(newVisible);
  };

  useEffect(() => {
    if (mode === "treeView" && expandedKeys.length > 0) {
      setInternalExpandedKeys(expandedKeys);
    }
  }, [expandedKeys, mode]);

  const handleChange = (selected: string | number | Array<string | number>) => {
    setUncontrolledValue(selected);
    if (onChange) onChange(fromInnerValue(selected));
  };

  const handleSearch = (value: string) => {
    if (onSearch) {
      onSearch(value);
    }
  };

  // rc-select's own "combobox" is the free-text mode; the prop's default `combobox` means
  // "an ordinary single select", which rc-select spells as no mode at all.
  let resolvedMode: "combobox" | "multiple" | "tags" | undefined;
  if (freeText) {
    resolvedMode = "combobox";
  } else if (mode !== "combobox" && mode !== "treeView") {
    resolvedMode = mode;
  }

  const filterOption = (input: string, option: any) => {
    if (customFilterOption) {
      return customFilterOption(input, option);
    }

    const optionData = options.find((opt) => opt.value === option.value);
    if (optionData?.searchLabel) {
      return optionData.searchLabel.toLowerCase().includes(input.toLowerCase());
    }

    if (typeof option?.children === "string") {
      return option.children.toLowerCase().includes(input.toLowerCase());
    }

    const textLabel = getStringFromReactNode(option?.children);
    if (textLabel) {
      return textLabel.toLowerCase().includes(input.toLowerCase());
    }

    return false;
  };

  const renderDropdownIcon = () => (
    <KuikaSvg
      name={isOpen ? "chevron-up" : "chevron-down"}
      type="arrows"
      width={12}
      height={12}
      color="rgba(96, 111, 133, 1)"
    />
  );

  const renderFilterResetButton = () => {
    const hasValue = Array.isArray(selectedValue)
      ? selectedValue.length > 0
      : selectedValue !== null && selectedValue !== undefined && selectedValue !== "";

    if (!showFilterResetButton || !hasValue) {
      return null;
    }

    return (
      <KuikaTooltip content={filterResetTooltip}>
        <div
          className="kuika-selectbox__reset-button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const clearedValue = mode === "multiple" || mode === "tags" || mode === "treeView" ? [] : null;
            setUncontrolledValue(clearedValue as any);
            if (onChange) onChange(clearedValue as any);
          }}
        >
          <KuikaSvg type="common" name="x" width={14} height={14} color="rgba(96, 111, 133, 1)" />
        </div>
      </KuikaTooltip>
    );
  };

  const renderClearIcon = () => (
    <KuikaSvg
      onClick={(e) => {
        e.stopPropagation();
        const clearedValue = mode === "multiple" || mode === "tags" || mode === "treeView" ? [] : null;
        setUncontrolledValue(clearedValue as any);
        if (onChange) onChange(clearedValue as any);
        if (onClear) onClear();
      }}
      name="x"
      width={12}
      height={12}
      color="rgba(96, 111, 133, 1)"
    />
  );

  const renderSelectedIcon = (option: { value: string | number; label: string }) => {
    const isSelected =
      mode === "multiple" || mode === "tags"
        ? Array.isArray(selectedValue) && selectedValue.includes(option.value)
        : selectedValue === toInnerValue(option.value);
    if (showOptionCheckboxes) {
      return (
        <div
          className={`kuika-selectbox__option-checkbox${isSelected ? " kuika-selectbox__option-checkbox--checked" : ""}`}
          style={{ pointerEvents: "none" }}
        >
          {isSelected && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M1.5 5L4 7.5L8.5 2.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      );
    }
    if (isSelected) {
      return <KuikaSvg name="check" type="common" width={16} height={16} color="rgba(36, 109, 255, 1)" />;
    }
    return null;
  };

  const defaultTagRender = (props: CustomTagProps) => {
    const { value, label, closable, onClose, isMaxTag } = props;

    if (isMaxTag) {
      return <>{label}</>;
    }
    let displayLabel = value;

    if (mode === "treeView") {
      // For tree view, find the label from tree data
      const findNodeLabel = (nodes: TreeNode[]): string | undefined => {
        for (const node of nodes) {
          if (node.value === value) return node.name;
          if (node.children) {
            const childLabel = findNodeLabel(node.children);
            if (childLabel) return childLabel;
          }
        }
        return undefined;
      };
      displayLabel = findNodeLabel(treeData) || value;
    } else {
      const option = options.find((opt) => opt.value === value);
      displayLabel = option ? option.label : value;
    }

    return (
      <span className="kuika-selectbox__tag">
        <span className="kuika-selectbox__tag-label">{displayLabel}</span>
        {closable && (
          <span className="kuika-selectbox__tag-close" onClick={onClose}>
            {showTagCrossIcon && (
              <KuikaSvg name="circle-x" type="common" width={12} height={12} color="rgba(96, 111, 133, 1)" />
            )}
          </span>
        )}
      </span>
    );
  };

  const handleExpandedKeysChange = (keys: string[]) => {
    setInternalExpandedKeys(keys);
    if (onExpandedKeysChange) {
      onExpandedKeysChange(keys);
    }
  };

  const toggleExpanded = (nodeId: string) => {
    const newExpandedKeys = internalExpandedKeys.includes(nodeId)
      ? internalExpandedKeys.filter((key) => key !== nodeId)
      : [...internalExpandedKeys, nodeId];
    handleExpandedKeysChange(newExpandedKeys);
  };

  const isNodeSelected = (nodeValue: string | number): boolean => {
    if (!Array.isArray(selectedValue)) return false;
    return selectedValue.includes(nodeValue);
  };

  const handleTreeNodeClick = (node: TreeNode, checked: boolean) => {
    if (node.type === "role" && node.value !== undefined) {
      let newSelectedValue: Array<string | number>;

      if (checked) {
        newSelectedValue = Array.isArray(selectedValue) ? [...selectedValue, node.value] : [node.value];
      } else {
        newSelectedValue = Array.isArray(selectedValue) ? selectedValue.filter((v) => v !== node.value) : [];
      }

      setUncontrolledValue(newSelectedValue);
      if (onChange) onChange(newSelectedValue);
      if (onTreeNodeSelect) onTreeNodeSelect(node, checked);
    }
  };

  const renderTreeNode = (node: TreeNode, level: number = 0): React.ReactNode => {
    const isExpanded = internalExpandedKeys.includes(node.id);
    const isSelected = node.value !== undefined ? isNodeSelected(node.value) : false;

    return (
      <div key={node.id} style={{ paddingLeft: level * 12 }}>
        <div className="kuika-tree-node">
          <div className="kuika-tree-node__content">
            {node.type === "folder" && (
              <>
                <div
                  className="kuika-tree-node__expand-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(node.id);
                  }}
                >
                  <KuikaSvg name={isExpanded ? "minus" : "plus"} type="common" width={8} height={8} />
                </div>
                <div
                  className="kuika-tree-node__folder-content"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(node.id);
                  }}
                >
                  <div className="kuika-tree-node__folder-icon">
                    <KuikaSvg name="folder" type="sidebar-icons" width={16} height={16} />
                  </div>
                  <KuikaText>{node.name}</KuikaText>
                </div>
              </>
            )}

            {node.type === "role" && (
              <div
                className="kuika-selectbox__tree-node-role-checkbox"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTreeNodeClick(node, !isSelected);
                }}
              >
                <KuikaCheckbox
                  checked={isSelected}
                  onChange={(e) => {
                    handleTreeNodeClick(node, e.target.checked);
                  }}
                  label={node.name}
                />
              </div>
            )}
          </div>
        </div>

        {node.type === "folder" && isExpanded && node.children && (
          <div className="kuika-tree-children">{node.children.map((child) => renderTreeNode(child, level + 1))}</div>
        )}
      </div>
    );
  };

  const filterTreeData = (nodes: TreeNode[], searchText: string): TreeNode[] => {
    if (!searchText) return nodes;

    return nodes
      .filter((node) => {
        if (node.type === "role") {
          return node.name.toLowerCase().includes(searchText.toLowerCase());
        }
        if (node.type === "folder") {
          const hasMatchingChildren = node.children && filterTreeData(node.children, searchText).length > 0;
          const folderMatches = node.name.toLowerCase().includes(searchText.toLowerCase());
          return folderMatches || hasMatchingChildren;
        }
        return false;
      })
      .map((node) => {
        if (node.type === "folder" && node.children) {
          return {
            ...node,
            children: filterTreeData(node.children, searchText)
          };
        }
        return node;
      });
  };

  const renderTreeView = (): React.ReactNode => {
    const filteredData = filterTreeData(treeData, searchValue || "");
    if (filteredData.length === 0 && notFoundContent) {
      return <div className="kuika-selectbox__tree-view-empty">{notFoundContent}</div>;
    }

    return (
      <div className="kuika-selectbox__tree-view-container">{filteredData.map((node) => renderTreeNode(node))}</div>
    );
  };

  const flatOptions: KuikaSelectboxOption[] =
    groups && groups.length > 0
      ? groups.flatMap((group: KuikaSelectboxOptionGroup): KuikaSelectboxOption[] => group.options)
      : options;

  const findOptionByValue = (candidate: unknown): KuikaSelectboxOption | undefined =>
    flatOptions.find((option: KuikaSelectboxOption): boolean => option.value === candidate);

  // A row carries its value as a data attribute, which is one of the few things rc-select 14
  // still forwards, and the list resolves the row under the pointer from there. Tracking the
  // last row keeps this to one call per row entered rather than one per pixel moved.
  const hoveredOptionValueRef = useRef<string | null>(null);

  const handleDropdownMouseOver = (event: React.MouseEvent<HTMLDivElement>): void => {
    const row: HTMLElement | null = (event.target as HTMLElement).closest(OPTION_VALUE_SELECTOR);
    const rowValue: string | null = row ? row.getAttribute(OPTION_VALUE_ATTRIBUTE) : null;
    if (rowValue === hoveredOptionValueRef.current) return;
    hoveredOptionValueRef.current = rowValue;
    if (rowValue === null || !onOptionMouseEnter) return;
    const hovered: KuikaSelectboxOption | undefined = flatOptions.find(
      (option: KuikaSelectboxOption): boolean => String(option.value) === rowValue
    );
    onOptionMouseEnter(hovered ? hovered.value : rowValue, hovered);
  };

  const handleDropdownMouseLeave = (): void => {
    hoveredOptionValueRef.current = null;
  };

  const renderDropdown = (menu: React.ReactElement): React.ReactElement => {
    const body: React.ReactElement = (
      <>
        {dropdownHeader && (
          <div
            className={classNames("kuika-selectbox__dropdown-header", dropdownHeaderClassName)}
            onMouseDown={(e) => {
              e.preventDefault();
              if (onDropdownHeaderClick) {
                onDropdownHeaderClick(e);
              }
            }}
          >
            <KuikaSmallBoldText>{dropdownHeader}</KuikaSmallBoldText>
          </div>
        )}
        {mode === "treeView" ? renderTreeView() : menu}
        {dropdownFooter && (
          <div className="kuika-selectbox__dropdown-footer">
            <KuikaSmallBoldText>{dropdownFooter}</KuikaSmallBoldText>
          </div>
        )}
      </>
    );

    if (!onOptionMouseEnter) return body;

    return (
      <div onMouseOver={handleDropdownMouseOver} onMouseLeave={handleDropdownMouseLeave}>
        {body}
      </div>
    );
  };

  const wrapperClassName = classNames(
    "kuika-selectbox",
    `kuika-selectbox--${size}`,
    `kuika-selectbox--${mode}`,
    className
  );

  const renderSuffixWithReset = () => {
    const resetButton = renderFilterResetButton();
    return resetButton ? (
      <div className="kuika-selectbox__suffix-wrapper">
        {resetButton}
        {suffixIcon || renderDropdownIcon()}
      </div>
    ) : (
      suffixIcon || renderDropdownIcon()
    );
  };

  // For tree view mode, we need to handle the Select component differently
  if (mode === "treeView") {
    return (
      <Select
        // Same as the main path below: the caller's own attributes (data-testid, aria-*) reach
        // rc-select's root, and every prop set explicitly here still wins.
        {...rest}
        className={wrapperClassName}
        dropdownClassName={classNames("kuika-selectbox__dropdown", "kuika-selectbox__dropdown--tree", overlayClassName)}
        transitionName="kuika-dropdown-motion"
        placeholder={placeholder}
        disabled={disabled}
        value={selectedValue}
        mode="multiple"
        onChange={handleChange as SelectProps["onChange"]}
        onSearch={handleSearch}
        searchValue={searchValue}
        suffixIcon={renderSuffixWithReset()}
        prefix={prefix || null}
        style={style}
        tagRender={tagRender || defaultTagRender}
        showSearch={searchable}
        dropdownRender={renderDropdown}
        maxTagCount={finalMaxTagCount}
        maxTagPlaceholder={finalMaxTagPlaceholder}
        allowClear={
          allowClear
            ? {
                clearIcon: renderClearIcon()
              }
            : false
        }
        onDropdownVisibleChange={handleVisibleChange}
        open={isOpen}
        getPopupContainer={resolvedGetPopupContainer}
      >
        {/* Empty options for tree view as we render custom dropdown */}
      </Select>
    );
  }

  // rc-select reports the single option that was picked, not the whole selection, so in a mode
  // whose selection is an array the value written here would replace the array with one entry.
  // `onChange` carries the full selection for those modes and fires on the same interaction.
  // `treeView` returns above, so only these two reach here.
  const isMultiValueMode: boolean = mode === "multiple" || mode === "tags";

  const handleSelect = (selected: string | number | Array<string | number>): void => {
    if (!isMultiValueMode) setUncontrolledValue(selected);
    if (!onSelect) return;
    const reported = fromInnerValue(selected) as string | number | null;
    onSelect(reported, findOptionByValue(reported));
  };

  const handleDeselect = (deselected: string | number | Array<string | number>): void => {
    if (!isMultiValueMode) setUncontrolledValue(deselected);
    if (!onDeselect) return;
    const reported = fromInnerValue(deselected) as string | number | null;
    onDeselect(reported, findOptionByValue(reported));
  };

  return (
    <Select
      {...rest}
      className={wrapperClassName}
      dropdownClassName={classNames(
        "kuika-selectbox__dropdown",
        overlayClassName,
        showOptionCheckboxes && "kuika-selectbox__dropdown--with-checkboxes"
      )}
      transitionName="kuika-dropdown-motion"
      placeholder={placeholder}
      disabled={disabled}
      value={
        mode === "tags" || mode === "multiple"
          ? Array.isArray(selectedValue) && selectedValue.length > 0
            ? selectedValue.filter((value) => !filterValue || filterValue(value))
            : selectedValue
          : selectedValue
      }
      defaultValue={defaultValue}
      mode={resolvedMode}
      onChange={handleChange as SelectProps["onChange"]}
      onSelect={handleSelect}
      onDeselect={handleDeselect}
      onSearch={handleSearch}
      searchValue={searchValue}
      suffixIcon={renderSuffixWithReset()}
      prefix={prefix || null}
      style={style}
      tagRender={tagRender || (mode === "tags" || mode === "multiple" ? defaultTagRender : undefined)}
      maxTagCount={finalMaxTagCount}
      maxTagPlaceholder={finalMaxTagPlaceholder}
      showSearch={searchable || freeText}
      filterOption={filterOption}
      menuItemSelectedIcon={showOptionCheckboxes ? () => null : renderSelectedIcon}
      dropdownRender={renderDropdown}
      notFoundContent={notFoundContent}
      tokenSeparators={tokenSeparators}
      onInputKeyDown={(e) => {
        // `tags` mode turns whatever is typed into a new tag on Enter. A consumer that creates tags
        // itself (`preventTagCreation`) needs that one key held back from rc-select — and only that
        // one. This used to stop EVERY key whose text was not already an option, which with an empty
        // search box is every key there is: rc-select's own handler then never saw Escape, and the
        // open list could not be closed with it.
        const wouldCreateTag: boolean =
          !!preventTagCreation &&
          e.key === "Enter" &&
          !options.find((opt) => opt.value === (e.target as HTMLInputElement).value);
        if (wouldCreateTag) {
          e.stopPropagation();
        }
        handleKeyDown?.(e);
      }}
      allowClear={
        allowClear
          ? {
              clearIcon: renderClearIcon()
            }
          : false
      }
      onDropdownVisibleChange={handleVisibleChange}
      open={isOpen}
      getPopupContainer={resolvedGetPopupContainer}
      id={id}
    >
      {(() => {
        const renderOption = (option: KuikaSelectboxOption, index: number, keyPrefix: string): React.ReactNode => {
          // `some` rather than `includes`: a selection is a list of the values actually chosen,
          // while an option's own value may be the null that stands for "no setting".
          const isChecked: boolean =
            showOptionCheckboxes === true &&
            (Array.isArray(selectedValue)
              ? selectedValue.some(
                  (selected: string | number): boolean =>
                    selected === option.value || selected === toInnerValue(option.value)
                )
              : selectedValue === toInnerValue(option.value));
          // Prefix the key with group label (when grouped) to avoid key collisions
          // across OptGroups when two groups share a value literal — only matters
          // if a caller passes duplicate values intentionally, but defensive.
          const fallbackKey: string = `${keyPrefix}${option.value}-${index}`;
          return (
            <Select.Option
              key={option.key || fallbackKey}
              value={toInnerValue(option.value)}
              disabled={option.disabled}
              data-testid={option.testId}
              {...(onOptionMouseEnter ? { [OPTION_VALUE_ATTRIBUTE]: String(option.value) } : {})}
            >
              {showOptionCheckboxes ? (
                <div className="kuika-selectbox__option-with-checkbox">
                  <div
                    className={`kuika-selectbox__option-checkbox${isChecked ? " kuika-selectbox__option-checkbox--checked" : ""}`}
                    style={{ pointerEvents: "none" }}
                  >
                    {isChecked && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path
                          d="M1.5 5L4 7.5L8.5 2.5"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  {option.label}
                </div>
              ) : (
                option.label
              )}
            </Select.Option>
          );
        };

        if (groups && groups.length > 0) {
          return groups.map((group) => (
            <Select.OptGroup key={group.label} label={group.label}>
              {group.options
                .filter((option) => !filterValue || filterValue(option.value))
                .map((option, index) => renderOption(option, index, `${group.label}__`))}
            </Select.OptGroup>
          ));
        }

        return options
          .filter((option) => !filterValue || filterValue(option.value))
          .map((option, index) => renderOption(option, index, ""));
      })()}
    </Select>
  );
};

export default KuikaSelectbox;
